import { screenSmallerThan } from '../../../shared/utilities/screenSize';

export function getFormatedAmount(amount, locale, currency) {
	const format = Intl.NumberFormat(locale);
	return currency ? `${format.format(amount)} ${currency}` : `${format.format(amount)}`;
}

function getTickInterval(maxReturnVal, yAxisTicks) {
	const lastVal = maxReturnVal;
	const denominator = yAxisTicks && yAxisTicks - 2 > 0 ? yAxisTicks - 1 : 2;
	const d = lastVal / denominator;
	const restArr = [...Array(yAxisTicks)].map((val, i) => Math.round(i * d));
	return [...restArr];
}

export function getSpaceValues() {
	const isMobileView = screenSmallerThan('md');
	const marginRight = isMobileView ? 10 : 64;
	const marginLeft = isMobileView ? 10 : 64;
	const marginTop = isMobileView ? 10 : 40;
	const marginBottom = isMobileView ? 0 : 64;
	return { marginLeft, marginRight, marginTop, marginBottom };
}

export function updateTooltipPosition(chart, lw) {
	if (screenSmallerThan('md')) {
		return { x: 0, y: 10 };
	}
	const yPos = chart.hoverPoints[0].plotY;
	let xPos = chart.hoverPoints[0].plotX;
	if (xPos > 400) {
		xPos -= (3 * lw) / 4; // to shift tooltip to left side of marker
	} else {
		xPos += 80; // tooltip in right side of marker
	}

	return {
		x: xPos,
		y: yPos
	};
}

export function getXAxisCategories(yearsCount, currentYear) {
	const tickPos = [];
	const xAxisInterval = Math.ceil(+yearsCount / 5);
	const maxYearValue = currentYear + Number(yearsCount);
	let year = currentYear;
	let i = 0;
	while (year < maxYearValue) {
		if (i !== 0) {
			tickPos.push(tickPos[i - 1] + xAxisInterval);
		} else {
			tickPos.push(0);
		}
		year += xAxisInterval;
		++i;
	}
	if (year === maxYearValue) {
		tickPos.push(tickPos[i - 1] + xAxisInterval);
	} else {
		const diff = year - maxYearValue;
		const lastValue = tickPos[i - 1] + xAxisInterval - diff;
		// if last label and second last label are far enough
		if (lastValue - tickPos[i - 1] < xAxisInterval / 2) {
			tickPos[i - 1] = lastValue;
		} else {
			tickPos.push(lastValue);
		}
	}
	return tickPos;
}

export function getChartOptions(
	propsForGraph,
	currentYear,
	yearsCount,
	returnValuesArray,
	totalDepositArray,
	isRTL
) {
	const { chartTextValues, yAxisTicks, colors, chartConstants } = propsForGraph;
	const { marginLeft, marginRight, marginTop, marginBottom } = getSpaceValues();

	const categories = [...Array(Number(yearsCount) + 1).keys()].map(x => x + currentYear);
	const currency = chartTextValues.currencyUnit;
	const locale = chartTextValues.culture;
	const tickPos = getTickInterval(returnValuesArray[returnValuesArray.length - 1], yAxisTicks);
	categories.splice(0, 1, chartTextValues.today);
	return {
		chart: {
			spacingRight: marginRight,
			spacingLeft: marginLeft,
			spacingTop: marginTop,
			spacingBottom: marginBottom,
			backgroundColor: '#E7EEFF'
		},
		accessibility: {
			enabled: true,
			keyboardNavigation: {
				enabled: true,
				focusBorder: {
					enabled: false
				}
			}
		},
		title: {
			text: null
		},

		subtitle: {
			text: null
		},
		tooltip: {
			backgroundColor: '#fff',
			shared: true,
			useHTML: true,
			shadow: false,
			borderRadius: 4,
			borderWidth: 1,
			borderColor: '#ced3d8',
			style: {
				zIndex: 999999
			},
			positioner(lw) {
				return updateTooltipPosition(this.chart, lw);
			},
			/* eslint-disable prettier/prettier */
			formatter() {				
				return `			
				<div class="v2-flexbox v2-paa8 tooltip" style="flex-direction:column;justify-content:center;z-index:999999;">
					<span class="v2-text-align--center v2-mab8 investment-calculator__tooltip-year">${this.x} </span> 
					<div class="v2-mab5 v2-flexbox justify-between" style="justify-content:flex-start;">
						<span class="investment-calculator__legend-color investment-calculator__legend-color--var1 v2-mar8"></span>						
						<span style="color:#4b5563;font-size:12px;line-height:14.5px;">${getFormatedAmount(this.points[0].y - this.points[1].y,locale,currency)}</span>
					</div>
					<div class="v2-mab5 v2-flexbox justify-between"  style="justify-content:flex-start;align-items:center;">
						<span class="investment-calculator__legend-color investment-calculator__legend-color--var2 v2-mar8"></span>
						<span style="color:#4b5563;font-size:12px;line-height:14.5px;">${getFormatedAmount(this.points[1].y,locale,currency)}</span>
					</div>
					<span class="v2-text-align--center v2-pat8 investment-calculator__tooltip-year" style="border-top:1px solid #e5e7eb;">${getFormatedAmount(this.points[0].y,locale,currency)}</span>
				</div>`;
			}
		},
		yAxis: {
			opposite: !isRTL,
			gridLineWidth: 0,
			tickPositions: tickPos,
			title: {
				enabled: false
			},
			labels: {
				enabled: !screenSmallerThan('md'),
				format: '{value}',
				formatter() {
					const yLbl = getFormatedAmount(this.value, locale, currency);
					return yLbl;
				}
			}
		},
		xAxis: {
			lineColor: '#dde0e4',
			reversed: !!isRTL,
			labels: {				
				y: 20,
				x: 0,
				autoRotation: false,
				rotation: 0
			},			
			tickPositions: screenSmallerThan('md') ? [0, +yearsCount] : getXAxisCategories(yearsCount, currentYear),			
			categories,
			plotLines: [
				{
					dashStyle: 'Solid',
					zIndex: 1
				}
			]			
		},
		legend: {
			enabled: false
		},
		credits: {
			enabled: false
		},
		plotOptions: {
			area: {				
				stacking: false,
				shadow: false,
				states: {
					inactive: {
						opacity: 1
					}
				}
			},
			series: {
				pointPlacement: 'on',
				marker: {
					enabled: false,
					symbol: 'circle',
					fillColor: '#fff',
					states: {
						hover: {
							enabled: true /* hover style on chart */,
							symbol: 'circle',
							radius: 7
						}
					}
				}
			}
		},
		responsive: {
			rules: [
				{
					condition: {
						maxWidth: 767
					},
					chartOptions: {
						chart: {
							height: chartConstants.chartHeight,
							spacing: [15, 10, 15, 10]
						}
					}
				}
			]
		},

		series: [
			{
				color: colors.returnsColor,
				data: returnValuesArray,
				id: 'series-return',
				type: 'area',
				name: 'Return',
				marker: {
					lineColor: colors.returnsColor
				}
			},
			{
				color: colors.depositColor,
				data: totalDepositArray,
				id: 'series-deposit',
				type: 'area',
				name: 'Deposit',
				marker: {
					lineColor: colors.depositColor
				}
			}
		]
	};
}

export function getUpdatedChartOptions(
	propsForGraph,
	currentYear,
	yearsCount,
	returnValuesArray,
	totalDepositArray
) {
	const categories = [...Array(Number(yearsCount) + 1).keys()].map(x => x + currentYear);
	const currency = propsForGraph.chartTextValues.currencyUnit;
	const locale = propsForGraph.chartTextValues.culture;
	const tickPos = getTickInterval(
		returnValuesArray[returnValuesArray.length - 1],
		propsForGraph.yAxisTicks
	);			
	const {marginLeft, marginRight, marginTop, marginBottom} = getSpaceValues();		
	categories.splice(0, 1, propsForGraph.chartTextValues.today);
	
	return {
		chart: {
			spacingRight: marginRight,
			spacingLeft: marginLeft,
			spacingBottom: marginBottom,
			spacingTop: marginTop
		},
		series: [
			{
				data: returnValuesArray,
				id: 'series-return'
			},
			{
				data: totalDepositArray,
				id: 'series-deposit'
			}
		],
		xAxis: {
			labels: {
				y: 20,
				x: 0,
				autoRotation: false,
				rotation: 0
			},
			categories,
			tickPositions: screenSmallerThan('md') ? [0, +yearsCount] : getXAxisCategories(yearsCount, currentYear)			
		},
		yAxis: {
			gridLineWidth: 0,
			tickPositions: tickPos,
			title: {
				enabled: false
			},
			labels: {
				enabled: !screenSmallerThan('md'),
				format: '{value}',
				formatter() {
					const yLbl = getFormatedAmount(this.value, locale, currency);											
					return yLbl;
				}
			}
		},
		tooltip: {
			positioner(lw) {				
				return updateTooltipPosition(this.chart,lw);
			}
		}
	};
}

export function fetchChartTextValues(dataset) {
	return {
		today: dataset.textToday || 'Today',
		year: dataset.textYear || 'Years',
		yearTextTooltip: dataset.textYearTooltip || 'Year',
		currencyUnit: dataset.currencyUnit || 'DKK',
		culture: dataset.culture || 'da-dk'
	};
}
